import React, { useState, useEffect, useRef } from "react";
import { PriceFormModal, formatPriceRange } from "../../user/Ads/components/FilterModal/PriceFormModal";
import { DateFormModal, formatDateRange } from "../../user/Ads/components/FilterModal/DateFormModal";
import { SelectFormModal } from "../../metrics/components/SelectFormModal";
import { Stack, ActionList, ActionMenu, Button, IconButton } from '@primer/react';
import { ChevronRightIcon, FilterIcon } from '@primer/octicons-react';
import { IntermediateFilterScreen } from "./IntermediateFilterScreen";
import { useFilterOptions } from "../../../hooks/useFilterOptions";
import { getAppliedColumnCountryIdFilters, getAppliedColumnCountryNameFilters, setAppliedColumnCountryIdFilters, setAppliedColumnCountryNameFilters, useAppliedColumnCountryIdFilters, useAppliedColumnCountryNameFilters, useAppliedColumnFilters } from "../ScoringFiltersManager";
import { FilterKey, FilterConfig, FilterValues, FilterCallbacks } from "../../../types/common/filters";

export interface ScoringFilterModalProps {
    filterOpened: boolean;
    setFilterOpened: (value: boolean) => void;
    priceFilterOpened: boolean;
    setPriceFilterOpened: (value: boolean) => void;
    dateFilterOpened: boolean;
    setDateFilterOpened: (value: boolean) => void;
    selectFilterOpened: string | null;
    setSelectFilterOpened: (value: string | null) => void;
    detailedSelectFilterOpened: string | null;
    setDetailedSelectFilterOpened: (value: string | null) => void;
    config: FilterConfig;
    values: FilterValues;
    callbacks: FilterCallbacks;
    onDismiss: () => void;
    onApply: () => void;
    isActive: boolean;
}

const getItemValueString = (itemValue: any): string => {
    if (typeof itemValue === 'string') return itemValue;

    // Для объектов где value это строка (города из синглтона)
    if (itemValue?.value && typeof itemValue.value === 'string') {
        return itemValue.value;
    }

    // Для вложенных объектов (страны)
    if (itemValue?.value?.value && typeof itemValue.value.value === 'string') {
        return itemValue.value.value;
    }

    // Для категорий/подкатегорий (имеют поле id)
    if (itemValue?.id && typeof itemValue.id === 'string') {
        return itemValue.id;
    }

    return '';
};

export const ScoringFilterModal: React.FC<ScoringFilterModalProps> = ({
    filterOpened,
    setFilterOpened,
    priceFilterOpened,
    setPriceFilterOpened,
    dateFilterOpened,
    setDateFilterOpened,
    selectFilterOpened,
    setSelectFilterOpened,
    detailedSelectFilterOpened,
    setDetailedSelectFilterOpened,
    config,
    values,
    callbacks,
    onDismiss,
    onApply,
    isActive
}) => {
    const {
        priceFilter = { isShow: true, label: "По цене" },
        dateFilter = { isShow: true, label: "По дате размещения", mode: 'full' },
        selectFilters = []
    } = config;

    const { onFilterChange, onPriceChange, onDateChange } = callbacks;

    const {
        loadedOptions,
        loadingStates,
        filterHasMore,
        filterLoadingMore,
        loadFilterOptions,
        onSearch,
        handleLoadMore,
        resetFilterData,
        getItems,
        getSelectedLabelForSubmenu,
        getGeographyLabel,
        getCategorySubcategoryLabel,
        getFilterGroups
    } = useFilterOptions({ values });

    const initialLoadDoneRef = useRef(false);

    const [selectedValues, setSelectedValues] = useState<Record<FilterKey, Set<string>>>({} as Record<FilterKey, Set<string>>);
    const [geographyScreenOpened, setGeographyScreenOpened] = useState(false);
    const [categorySubcategoryScreenOpened, setCategorySubcategoryScreenOpened] = useState(false);

    const selectedCountryName = values['countryResponseForName'];
    const selectedCountryId = values['countryResponseForId'];
    const selectedCategoryId = values['category'];

    const loadFilterOptionsRef = useRef(loadFilterOptions);
    useEffect(() => {
        loadFilterOptionsRef.current = loadFilterOptions;
    }, [loadFilterOptions]);

    useEffect(() => {
        if (!filterOpened) return;

        const currentCountryNameFilters = getAppliedColumnCountryNameFilters();
        const currentCountryIdFilters = getAppliedColumnCountryIdFilters();

        // Синхронизирует countryResponseForName из синглтона
        if (currentCountryNameFilters.currentCountry && currentCountryNameFilters.currentCountry.length > 0) {
            const allCountries = currentCountryNameFilters.currentCountry;

            const valueSet = new Set<string>();
            allCountries.forEach(country => {
                const val = getItemValueString(country.value);
                if (val) valueSet.add(val);
            });

            if (valueSet.size > 0) {
                setSelectedValues(prev => ({
                    ...prev,
                    countryResponseForName: valueSet
                }));
            }

            if (!values.countryResponseForName?.length) {
                onFilterChange('countryResponseForName', allCountries);
            }
        }

        // Синхронизируем countryResponseForId из синглтона
        if (currentCountryIdFilters.currentCountry && currentCountryIdFilters.currentCountry.length > 0) {
            const allCountries = currentCountryIdFilters.currentCountry;

            const valueSet = new Set<string>();
            allCountries.forEach(country => {
                const val = getItemValueString(country.value);
                if (val) valueSet.add(val);
            });

            if (valueSet.size > 0) {
                setSelectedValues(prev => ({
                    ...prev,
                    countryResponseForId: valueSet
                }));
            }

            if (!values.countryResponseForId?.length) {
                onFilterChange('countryResponseForId', allCountries);
            }
        }

        // Синхронизируем cityResponseForName из синглтона
        if (currentCountryNameFilters.currentCity && currentCountryNameFilters.currentCity.length > 0) {
            const cities = currentCountryNameFilters.currentCity;

            const valueSet = new Set<string>();
            cities.forEach(city => {
                const val = typeof city.value === 'string' ? city.value : getItemValueString(city);
                if (val) valueSet.add(val);
            });

            if (valueSet.size > 0) {
                setSelectedValues(prev => ({
                    ...prev,
                    cityResponseForName: valueSet
                }));
            }

            if (!values.cityResponseForName?.length) {
                onFilterChange('cityResponseForName', cities);
            }
        }

        // Синхронизируем cityResponseForId из синглтона
        if (currentCountryIdFilters.currentCity && currentCountryIdFilters.currentCity.length > 0) {
            const cities = currentCountryIdFilters.currentCity;

            const valueSet = new Set<string>();
            cities.forEach(city => {
                const val = typeof city.value === 'string' ? city.value : getItemValueString(city);
                if (val) valueSet.add(val);
            });

            if (valueSet.size > 0) {
                setSelectedValues(prev => ({
                    ...prev,
                    cityResponseForId: valueSet
                }));
            }

            if (!values.cityResponseForId?.length) {
                onFilterChange('cityResponseForId', cities);
            }
        }
    }, [filterOpened]);

    useEffect(() => {
        if (selectedCountryId) {
            onFilterChange('cityResponseForId', []);
            resetFilterData('cityResponseForId');
        }
    }, [selectedCountryId]);

    useEffect(() => {
        if (selectedCountryName && selectedCountryName.length > 0) {
            if (values['cityResponseForName'] && values['cityResponseForName'].length > 0) {
                onFilterChange('cityResponseForName', []);
                resetFilterData('cityResponseForName');
            }
        }
    }, [selectedCountryName?.length]);

    useEffect(() => {
        if (selectedCategoryId) {
            resetFilterData('subcategory');
        }
    }, [selectedCategoryId]);

    const fromPrice = values.price?.from || '';
    const toPrice = values.price?.to || '';
    const fromDate = values.date?.from || '';
    const toDate = values.date?.to || '';

    const fromPriceNumber = parseFloat(fromPrice);
    const toPriceNumber = parseFloat(toPrice);

    const priceFormError = fromPrice !== '' && toPrice !== '' && !isNaN(fromPriceNumber) && !isNaN(toPriceNumber) && fromPriceNumber > toPriceNumber;
    const dateFormError = fromDate !== '' && toDate !== '' && new Date(fromDate) > new Date(toDate);

    const detailedFilters: FilterKey[] = ['countryResponseForName', 'cityResponseForName', 'category', 'subcategory', 'filterName', 'scoringUserType'];
    const singleSelectFilters: FilterKey[] = ['hint', 'userType'];

    const handleSelect = (filterKey: FilterKey, value: string) => {
        const items = loadedOptions[filterKey] || [];
        const item = items.find(i => {
            const itemValue = getItemValueString(i.value);
            return itemValue === value;
        });

        console.log('handleSelect', filterKey, value);

        setSelectedValues(prev => {
            const newSet = new Set(prev[filterKey] || new Set<string>());
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }

            const newValues = { ...prev, [filterKey]: newSet };

            // Когда изменяется страна, очищаем selectedValues для городов
            if (filterKey === 'countryResponseForName') {
                newValues.cityResponseForName = new Set<string>();
            }
            if (filterKey === 'category') {
                newValues.subcategory = new Set<string>();
            }

            return newValues;
        });

        const currentValues = values[filterKey] || [];
        let newValues;

        if (Array.isArray(currentValues)) {
            const exists = currentValues.some(v => {
                const vValue = getItemValueString(v);
                const itemValue = getItemValueString(item?.value);
                return vValue === itemValue || v?.label === item?.label;
            });

            if (exists) {
                newValues = currentValues.filter(v => {
                    const vValue = getItemValueString(v);
                    const itemValue = getItemValueString(item?.value);
                    return vValue !== itemValue && v?.label !== item?.label;
                });
            } else {
                newValues = [...currentValues, item?.value];
            }
        } else {
            newValues = [item?.value];
        }

        onFilterChange(filterKey, newValues);
    };

    const priceModalDidDismiss = () => {
        setPriceFilterOpened(false);
        setFilterOpened(true);
    };

    const dateModalDidDismiss = () => {
        setDateFilterOpened(false);
        setFilterOpened(true);
    };

    const selectDetailedModalDidDismiss = () => {
        setDetailedSelectFilterOpened(null);
        setFilterOpened(true);
    };

    const selectModalDidDismiss = () => {
        setSelectFilterOpened(null);
        setFilterOpened(true);
    };

    const handleDismiss = () => {
        setFilterOpened(false);
        setSelectedValues({} as Record<FilterKey, Set<string>>);
        onDismiss();
    };

    const handleApply = () => {
        // Формируем объект с фильтрами для countryName
        const countryNameFilters: Record<string, any[]> = {};
        if (values.countryResponseForName && values.countryResponseForName.length > 0) {
            countryNameFilters.currentCountry = values.countryResponseForName;
        }
        if (values.cityResponseForName && values.cityResponseForName.length > 0) {
            countryNameFilters.currentCity = values.cityResponseForName;
        }
        setAppliedColumnCountryNameFilters(countryNameFilters);

        // Формируем объект с фильтрами для countryId
        const countryIdFilters: Record<string, any[]> = {};
        if (values.countryResponseForId && values.countryResponseForId.length > 0) {
            countryIdFilters.currentCountry = values.countryResponseForId;
        }
        if (values.cityResponseForId && values.cityResponseForId.length > 0) {
            countryIdFilters.currentCity = values.cityResponseForId;
        }
        setAppliedColumnCountryIdFilters(countryIdFilters);

        setFilterOpened(false);
        onApply();
    };

    const handlePriceChange = (from: string, to: string) => {
        onPriceChange(from, to);
    };

    const geographyScreenDidDismiss = () => {
        setGeographyScreenOpened(false);
    };

    const categorySubcategoryScreenDidDismiss = () => {
        setCategorySubcategoryScreenOpened(false);
    };

    return (
        <>
            {priceFilter.isShow && (
                <PriceFormModal
                    priceFilterOpened={priceFilterOpened}
                    setPriceFilterOpened={setPriceFilterOpened}
                    fromPrice={fromPrice}
                    setFromPrice={(value) => handlePriceChange(value, toPrice)}
                    toPrice={toPrice}
                    setToPrice={(value) => handlePriceChange(fromPrice, value)}
                    priceFormError={priceFormError}
                    onDismiss={priceModalDidDismiss}
                    onApply={priceModalDidDismiss}
                />
            )}

            {dateFilter.isShow && (
                <DateFormModal
                    dateFilterOpened={dateFilterOpened}
                    setDateFilterOpened={setDateFilterOpened}
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={(value) => onDateChange(value, toDate)}
                    setToDate={(value) => onDateChange(fromDate, value)}
                    dateFormError={dateFormError}
                    onDismiss={dateModalDidDismiss}
                    onApply={dateModalDidDismiss}
                    onClearDates={() => onDateChange('', '')}
                    isShowBackButton={true}
                    mode={dateFilter.mode}
                />
            )}

            {geographyScreenOpened && (
                <IntermediateFilterScreen
                    opened={geographyScreenOpened}
                    setOpened={setGeographyScreenOpened}
                    title="География"
                    items={[
                        {
                            key: 'countryResponseForName',
                            label: 'Страна',
                            labelSearchPlaceholder: 'Поиск страны',
                            selectedLabel: getSelectedLabelForSubmenu({ key: 'countryResponseForName', label: 'Страна', options: [] }, selectedValues),
                            onSelect: () => { }
                        },
                        {
                            key: 'cityResponseForName',
                            label: 'Город',
                            labelSearchPlaceholder: 'Поиск города',
                            selectedLabel: getSelectedLabelForSubmenu({ key: 'cityResponseForName', label: 'Город', options: [] }, selectedValues),
                            onSelect: () => { }
                        }
                    ]}
                    onDismiss={geographyScreenDidDismiss}
                    loadedOptions={loadedOptions}
                    selectedValues={selectedValues}
                    handleSelect={handleSelect}
                    filterHasMore={filterHasMore}
                    filterLoadingMore={filterLoadingMore}
                    handleLoadMore={handleLoadMore}
                    loadingStates={loadingStates}
                    resetFilterData={resetFilterData}
                    loadFilterOptions={loadFilterOptions}
                    onSearch={onSearch}
                />
            )}

            <IntermediateFilterScreen
                opened={categorySubcategoryScreenOpened}
                setOpened={setCategorySubcategoryScreenOpened}
                title="Раздел / подраздел"
                items={[
                    {
                        key: 'category',
                        label: 'Раздел',
                        labelSearchPlaceholder: 'Поиск раздела',
                        selectedLabel: getSelectedLabelForSubmenu({ key: 'category', label: 'Раздел', options: [] }, selectedValues),
                        onSelect: () => { }
                    },
                    {
                        key: 'subcategory',
                        label: 'Подраздел',
                        labelSearchPlaceholder: 'Поиск подраздела',
                        selectedLabel: getSelectedLabelForSubmenu({ key: 'subcategory', label: 'Подраздел', options: [] }, selectedValues),
                        onSelect: () => { }
                    }
                ]}
                onDismiss={categorySubcategoryScreenDidDismiss}
                loadedOptions={loadedOptions}
                selectedValues={selectedValues}
                handleSelect={handleSelect}
                filterHasMore={filterHasMore}
                filterLoadingMore={filterLoadingMore}
                handleLoadMore={handleLoadMore}
                loadingStates={loadingStates}
                resetFilterData={resetFilterData}
                loadFilterOptions={loadFilterOptions}
                onSearch={onSearch}
            />

            {selectFilters.map((selectFilter) => {
                if (!detailedFilters.includes(selectFilter.key)) return null;

                const items = loadedOptions[selectFilter.key] || [];
                const isLoading = loadingStates[selectFilter.key];
                const currentSelectedValues = selectedValues[selectFilter.key] || new Set<string>([]);

                return (
                    <SelectFormModal
                        key={selectFilter.key + '-modal'}
                        selectFilterOpened={detailedSelectFilterOpened === selectFilter.key}
                        setSelectFilterOpened={(isOpen) => setDetailedSelectFilterOpened(isOpen ? selectFilter.key : null)}
                        label={selectFilter.label}
                        items={items}
                        isGrouped={selectFilter.key === 'scoringUserType'}
                        groups={selectFilter.key === 'scoringUserType' ? getFilterGroups(selectFilter.key) : undefined}
                        selectedValues={currentSelectedValues}
                        onSelect={(value) => handleSelect(selectFilter.key, value)}
                        onDismiss={selectDetailedModalDidDismiss}
                        isLoading={isLoading}
                        hasMore={filterHasMore[selectFilter.key]}
                        isLoadingMore={filterLoadingMore[selectFilter.key]}
                        onLoadMore={() => handleLoadMore(selectFilter.key)}
                    />
                );
            })}

            {selectFilters.map((selectFilter) => {
                if (detailedFilters.includes(selectFilter.key)) return null;

                return (
                    <ActionMenu
                        key={selectFilter.key}
                        open={selectFilterOpened === selectFilter.key}
                        onOpenChange={(isOpen) => setSelectFilterOpened(isOpen ? selectFilter.key : null)}
                    >
                        <ActionMenu.Overlay className="!w-[260px]">
                            <ActionList
                                selectionVariant="single"
                                role="menu"
                                aria-label={selectFilter.label}
                                className="!w-[260px]"
                            >
                                {selectFilter.options.map((option, index) => (
                                    <ActionList.Item
                                        key={index}
                                        role="menuitemradio"
                                        selected={option.value === values[selectFilter.key]}
                                        aria-checked={option.value === values[selectFilter.key]}
                                        onSelect={() => {
                                            onFilterChange(selectFilter.key, option.value);
                                            selectModalDidDismiss();
                                        }}
                                    >
                                        <Stack align="center" direction="horizontal" gap='none'>
                                            {option.label}
                                        </Stack>
                                    </ActionList.Item>
                                ))}
                            </ActionList>
                        </ActionMenu.Overlay>
                    </ActionMenu>
                );
            })}

            <ActionMenu open={filterOpened} onOpenChange={setFilterOpened}>
                <ActionMenu.Anchor>
                    <div className="relative">
                        <IconButton
                            icon={FilterIcon}
                            aria-label="Фильтр"
                            className={isActive ? '!text-[#0969DA]' : '!text-[#59636E]'}
                            onClick={() => setFilterOpened(true)}
                        />
                        {isActive && (
                            <div className="absolute top-[3px] right-[3px] w-[6px] h-[6px] bg-[#FB3535] rounded-full" />
                        )}
                    </div>
                </ActionMenu.Anchor>
                <ActionMenu.Overlay className="!w-[260px]">
                    <ActionList role="menu" aria-label="Item" className="!w-[260px]">
                        <ActionList.Group>
                            {priceFilter.isShow && (
                                <ActionList.Item key='price' onSelect={() => setPriceFilterOpened(true)}>
                                    <p className='font-normal'>{priceFilter.label}</p>
                                    <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                    <ActionList.Description variant="block">
                                        {formatPriceRange(fromPrice, toPrice)}
                                    </ActionList.Description>
                                </ActionList.Item>
                            )}

                            {dateFilter.isShow && (
                                <ActionList.Item key='date' onSelect={() => setDateFilterOpened(true)}>
                                    {dateFilter.icon && <ActionList.LeadingVisual><dateFilter.icon /></ActionList.LeadingVisual>}
                                    <p className='font-normal whitespace-pre-line'>{dateFilter.label}</p>
                                    <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                    <ActionList.Description variant="block">
                                        {formatDateRange(fromDate, toDate, dateFilter.mode)}
                                    </ActionList.Description>
                                </ActionList.Item>
                            )}

                            {selectFilters.map((selectFilter) => {
                                const items = getItems(selectFilter);
                                const isLoading = loadingStates[selectFilter.key];
                                const isDetailedFilter = detailedFilters.includes(selectFilter.key);
                                const isSingleSelect = singleSelectFilters.includes(selectFilter.key);

                                if (selectFilter.key === 'geography') {
                                    return (
                                        <ActionList.Item key={selectFilter.key} onSelect={(e) => {
                                            e.preventDefault();
                                            setGeographyScreenOpened(true);
                                        }}>
                                            {selectFilter.icon && <ActionList.LeadingVisual><selectFilter.icon /></ActionList.LeadingVisual>}
                                            <p className='font-normal'>{selectFilter.label}</p>
                                            <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                            <ActionList.Description variant="block">
                                                {getGeographyLabel(selectedValues)}
                                            </ActionList.Description>
                                        </ActionList.Item>
                                    );
                                }

                                if (selectFilter.key === 'category-subcategory') {
                                    return (
                                        <ActionList.Item key={selectFilter.key} onSelect={(e) => {
                                            e.preventDefault();
                                            setCategorySubcategoryScreenOpened(true);
                                        }}>
                                            {selectFilter.icon && <ActionList.LeadingVisual><selectFilter.icon /></ActionList.LeadingVisual>}
                                            <p className='font-normal'>{selectFilter.label}</p>
                                            <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                            <ActionList.Description variant="block">
                                                {getCategorySubcategoryLabel(selectedValues)}
                                            </ActionList.Description>
                                        </ActionList.Item>
                                    );
                                }

                                if (isDetailedFilter) {
                                    return (
                                        <ActionList.Item
                                            key={selectFilter.key}
                                            onSelect={() => {
                                                resetFilterData(selectFilter.key);
                                                if ((
                                                    selectFilter.key === 'cityResponseForName' ||
                                                    selectFilter.key === 'cityResponseForId' ||
                                                    selectFilter.key === 'category' ||
                                                    selectFilter.key === 'subcategory' ||
                                                    selectFilter.key === 'scoringUserType'
                                                ) || selectFilter.options.length === 0) {
                                                    loadFilterOptions(selectFilter.key, false, true);
                                                }
                                                setDetailedSelectFilterOpened(selectFilter.key);
                                            }}
                                        >
                                            {selectFilter.icon && <ActionList.LeadingVisual><selectFilter.icon /></ActionList.LeadingVisual>}
                                            <p className='font-normal'>{selectFilter.label}</p>
                                            <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                            <ActionList.Description variant="block">
                                                {getSelectedLabelForSubmenu(selectFilter, selectedValues)}
                                            </ActionList.Description>
                                        </ActionList.Item>
                                    );
                                }

                                return (
                                    <ActionMenu
                                        key={selectFilter.key}
                                        onOpenChange={(open) => {
                                            if (selectFilter.options.length === 0) {
                                                loadFilterOptions(selectFilter.key);
                                            }
                                        }}
                                    >
                                        <ActionMenu.Anchor>
                                            <ActionList.Item>
                                                {selectFilter.icon && <ActionList.LeadingVisual><selectFilter.icon /></ActionList.LeadingVisual>}
                                                <p className='font-normal'>{selectFilter.label}</p>
                                                <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                                <ActionList.Description variant="block">
                                                    {isSingleSelect
                                                        ? (items.find(item => item.value === values[selectFilter.key])?.label || '')
                                                        : getSelectedLabelForSubmenu(selectFilter, selectedValues)
                                                    }
                                                </ActionList.Description>
                                            </ActionList.Item>
                                        </ActionMenu.Anchor>
                                        <ActionMenu.Overlay>
                                            <ActionList
                                                selectionVariant={isSingleSelect ? "single" : "multiple"}
                                                role="menu"
                                                aria-label="Item"
                                            >
                                                {isLoading ? (
                                                    <ActionList.Item loading>Загрузка...</ActionList.Item>
                                                ) : items.length === 0 ? (
                                                    <ActionList.Item>Нет данных</ActionList.Item>
                                                ) : (
                                                    items.map((item, index) => {
                                                        const itemValueString = getItemValueString(item.value);
                                                        const isSelected = isSingleSelect
                                                            ? values[selectFilter.key] === item.value
                                                            : selectedValues[selectFilter.key]?.has(itemValueString);

                                                        return (
                                                            <ActionList.Item
                                                                key={item.label + '-submenu'}
                                                                role="menuitemradio"
                                                                selected={isSelected}
                                                                aria-checked={isSelected}
                                                                onSelect={(e) => {
                                                                    e.preventDefault();
                                                                    if (isSingleSelect) {
                                                                        const newValue = values[selectFilter.key] === item.value ? null : item.value;
                                                                        onFilterChange(selectFilter.key, newValue);
                                                                    } else {
                                                                        handleSelect(selectFilter.key, itemValueString);
                                                                    }
                                                                }}
                                                            >
                                                                {item.label}
                                                            </ActionList.Item>
                                                        );
                                                    })
                                                )}
                                            </ActionList>
                                        </ActionMenu.Overlay>
                                    </ActionMenu>
                                );
                            })}
                        </ActionList.Group>

                        <Stack direction="horizontal" gap='normal' className='block mt-4 ml-[66px]'>
                            <Button onClick={handleDismiss} size='small' variant="default">
                                Очистить
                            </Button>
                            <Button onClick={handleApply} size='small' variant="primary">
                                Применить
                            </Button>
                        </Stack>
                    </ActionList>
                </ActionMenu.Overlay>
            </ActionMenu>
        </>
    );
};